import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { templateAPI } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  FileText, Award, ScrollText, Stamp, Bell,
  Search, Loader2, Eye, Copy, Shield, Layers, X, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  minutes:     { label: 'Minutes',     icon: FileText,   color: '#00A3A3', bg: '#00A3A3' },
  resolution:  { label: 'Resolution',  icon: Award,      color: '#F58A26', bg: '#F58A26' },
  certificate: { label: 'Certificate', icon: Stamp,      color: '#7C3AED', bg: '#7C3AED' },
  notice:      { label: 'Notice',      icon: Bell,       color: '#2563EB', bg: '#2563EB' },
  agenda:      { label: 'Agenda',      icon: ScrollText, color: '#059669', bg: '#059669' },
  other:       { label: 'Other',       icon: Layers,     color: '#6B7280', bg: '#6B7280' },
};

const TemplateGallery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cloning, setCloning] = useState(null);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await templateAPI.getAll();
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handlePreview = async (tmpl) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    try {
      const res = await templateAPI.preview(tmpl.id);
      setPreviewData({ html: res.data.rendered_html, name: res.data.template_name, tmpl });
    } catch { toast.error('Preview failed'); }
    finally { setPreviewLoading(false); }
  };

  const handleClone = async (tmpl) => {
    setCloning(tmpl.id);
    try {
      await templateAPI.clone(tmpl.id);
      toast.success(`Cloned "${tmpl.name}" — you can now customize it`);
      fetchTemplates();
    } catch { toast.error('Failed to clone template'); }
    finally { setCloning(null); }
  };

  const handleUse = (tmpl) => {
    const type = tmpl.template_type;
    if (type === 'minutes') navigate('/minutes');
    else if (type === 'certificate') navigate('/certificates/new');
    else if (type === 'resolution') navigate('/resolutions');
    else if (type === 'notice') navigate('/meetings');
    else navigate('/admin?tab=templates');
  };

  const filtered = templates.filter(t => {
    if (filter !== 'all' && t.template_type !== filter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const types = ['all', ...Object.keys(TYPE_META)];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="template-gallery">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Template Gallery</h1>
          <p className="text-[#5A6570] mt-1">
            Browse, preview, and apply document templates. Custom templates override platform defaults.
          </p>
        </div>
        <Button
          className="bg-[#00A3A3] hover:bg-[#008585] text-white shrink-0"
          onClick={() => navigate('/templates/builder')}
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8B1B9]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#E2E6EA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3A3]/30 focus:border-[#00A3A3]"
            data-testid="gallery-search"
          />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {types.map(t => {
            const meta = TYPE_META[t];
            const active = filter === t;
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  active
                    ? 'text-white border-transparent shadow-sm'
                    : 'text-[#5A6570] border-[#E2E6EA] bg-white hover:border-[#00A3A3] hover:text-[#00A3A3]'
                }`}
                style={active ? { backgroundColor: t === 'all' ? '#1A1D21' : meta?.bg, borderColor: 'transparent' } : {}}
                data-testid={`filter-${t}`}
              >
                {t === 'all' ? 'All' : meta?.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-6 text-sm text-[#5A6570]">
        <span>{filtered.length} template{filtered.length !== 1 ? 's' : ''}</span>
        <span>{filtered.filter(t => t.scope === 'TENANT_CUSTOM').length} custom</span>
        <span>{filtered.filter(t => t.scope === 'LANDLORD_DEFAULT').length} defaults</span>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="py-16 text-center">
            <Layers className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
            <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No templates found</h3>
            <p className="text-[#5A6570]">
              {search ? 'Try a different search term' : 'Templates will appear here once your admin sets them up'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
          {filtered.map(tmpl => {
            const meta = TYPE_META[tmpl.template_type] || TYPE_META.other;
            const Icon = meta.icon;
            const isCustom = tmpl.scope === 'TENANT_CUSTOM';
            const isSEC = tmpl.source === 'SEC_RECOMMENDED';

            return (
              <Card
                key={tmpl.id}
                className={`group relative overflow-hidden border transition-all hover:shadow-md ${
                  isCustom ? 'border-[#00A3A3]/30' : 'border-[#E2E6EA]'
                }`}
                data-testid={`gallery-card-${tmpl.id}`}
              >
                {/* Color strip top */}
                <div className="h-1.5" style={{ backgroundColor: meta.color }} />

                <CardContent className="p-5">
                  {/* Icon + badges */}
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="p-2.5 rounded-lg shrink-0"
                      style={{ backgroundColor: `${meta.color}12` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#1A1D21] text-sm leading-tight truncate" title={tmpl.name}>
                        {tmpl.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {isCustom ? (
                          <Badge className="text-[10px] px-1.5 py-0 bg-[#00A3A3]/10 text-[#00A3A3] border-[#00A3A3]/20 font-medium">
                            Custom
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-[#A8B1B9] font-normal">
                            Default
                          </Badge>
                        )}
                        {isSEC && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-[#F58A26]/10 text-[#F58A26] border-[#F58A26]/20 font-medium">
                            <Shield className="h-2.5 w-2.5 mr-0.5" />SEC
                          </Badge>
                        )}
                        {tmpl.applicable_meeting_types?.map(mt => (
                          <Badge key={mt} variant="outline" className="text-[10px] px-1.5 py-0 uppercase font-normal">
                            {mt}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Placeholder preview snippet */}
                  <div className="text-xs text-[#A8B1B9] mb-4 line-clamp-2 leading-relaxed">
                    {tmpl.body_html
                      ? tmpl.body_html.replace(/<[^>]*>/g, '').replace(/\{\{[^}]+\}\}/g, '____').substring(0, 120) + '...'
                      : 'No content yet'}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-8 text-xs border-[#E2E6EA] hover:border-[#00A3A3] hover:text-[#00A3A3]"
                      onClick={() => handlePreview(tmpl)}
                      data-testid={`preview-${tmpl.id}`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Preview
                    </Button>

                    {!isCustom && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs border-[#E2E6EA] hover:border-[#00A3A3] hover:text-[#00A3A3]"
                        disabled={cloning === tmpl.id}
                        onClick={() => handleClone(tmpl)}
                        data-testid={`clone-${tmpl.id}`}
                      >
                        {cloning === tmpl.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                        Clone
                      </Button>
                    )}

                    <Button
                      size="sm"
                      className="h-8 text-xs text-white"
                      style={{ backgroundColor: meta.color }}
                      onClick={() => handleUse(tmpl)}
                      data-testid={`use-${tmpl.id}`}
                    >
                      Use
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[#00A3A3]" />
              {previewData?.name || 'Template Preview'}
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
            </div>
          ) : previewData ? (
            <div className="flex-1 overflow-auto">
              <div className="border border-[#E2E6EA] rounded-lg bg-white p-6 shadow-inner">
                <div
                  className="prose max-w-none font-serif text-sm"
                  dangerouslySetInnerHTML={{ __html: previewData.html }}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4 pb-2">
                {previewData.tmpl?.scope === 'LANDLORD_DEFAULT' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { handleClone(previewData.tmpl); setPreviewOpen(false); }}
                    className="border-[#E2E6EA]"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Clone & Customize
                  </Button>
                )}
                <Button
                  size="sm"
                  className="bg-[#00A3A3] hover:bg-[#008585]"
                  onClick={() => { handleUse(previewData.tmpl); setPreviewOpen(false); }}
                >
                  Use This Template
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateGallery;
